import React, { useState, useEffect, useMemo, useRef } from "react";
import { Modal, Checkbox, Button, Space, Spin, notification, Input } from "antd";
import { useQuery } from "@tanstack/react-query";
import { fetchProjectUsers } from "../services/projectsAPI";
import { useAuth } from "../hooks/useAuth";
import { getDefaultNotifyUserIdsWithEmail } from "../utils/notifyDefaultUserIds";
import { convertRole } from "../utils/textUtils";

const { TextArea } = Input;

const NotifyUsersModal = ({
  visible,
  onClose,
  folder,
  onNotify,
  itemName,
  showNote = false,
  defaultUserIds,
  defaultUserIdsLoading = false,
}) => {
  const { selectedProject } = useAuth();
  const projectId = selectedProject?.project_id;
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [note, setNote] = useState("");
  const wasVisibleRef = useRef(false);
  const defaultsAppliedRef = useRef(false);

  // Fetch project users
  const {
    data: usersResponse,
    isLoading: usersLoading,
    isError: usersError,
  } = useQuery({
    queryKey: ["projectUsers", projectId],
    queryFn: () => fetchProjectUsers(projectId, 1, 1000),
    enabled: visible && !!projectId,
    onError: () => {
      notification.error({
        message: "Failed to load users",
        description: "Could not fetch project users. Please try again.",
      });
    },
  });

  const users = usersResponse?.data || [];
  const isContentLoading = usersLoading || defaultUserIdsLoading;

  const filteredUsers = useMemo(() => {
    let list = users;
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      list = users.filter((user) => {
        const roleLabel =
          user.formatted_role || convertRole(user.role) || user.role || "";
        return (
          user.first_name?.toLowerCase().includes(searchLower) ||
          user.last_name?.toLowerCase().includes(searchLower) ||
          user.email?.toLowerCase().includes(searchLower) ||
          user.username?.toLowerCase().includes(searchLower) ||
          user.vendor_name?.toLowerCase().includes(searchLower) ||
          roleLabel.toLowerCase().includes(searchLower) ||
          user.role?.toLowerCase().includes(searchLower)
        );
      });
    }

    if (!defaultUserIds?.length) {
      return list;
    }

    const defaultOrder = new Map(
      defaultUserIds.map((id, index) => [String(id), index]),
    );

    return [...list].sort((a, b) => {
      const aId = String(a.id || a._id);
      const bId = String(b.id || b._id);
      const aRank = defaultOrder.has(aId) ? defaultOrder.get(aId) : Number.MAX_SAFE_INTEGER;
      const bRank = defaultOrder.has(bId) ? defaultOrder.get(bId) : Number.MAX_SAFE_INTEGER;
      if (aRank !== bRank) {
        return aRank - bRank;
      }
      const aName = (
        a.username || `${a.first_name || ""} ${a.last_name || ""}`.trim() || a.email || ""
      ).toLowerCase();
      const bName = (
        b.username || `${b.first_name || ""} ${b.last_name || ""}`.trim() || b.email || ""
      ).toLowerCase();
      return aName.localeCompare(bName);
    });
  }, [users, searchText, defaultUserIds]);

  // Reset on close; pre-select defaults at most once per open (after loading).
  useEffect(() => {
    if (!visible) {
      if (wasVisibleRef.current) {
        setSelectedUserIds([]);
        setSelectAll(false);
        setSearchText("");
        if (showNote) {
          setNote("");
        }
      }
      wasVisibleRef.current = false;
      defaultsAppliedRef.current = false;
      return;
    }

    const justOpened = !wasVisibleRef.current;
    wasVisibleRef.current = true;

    if (justOpened) {
      defaultsAppliedRef.current = false;
      setSearchText("");
      if (showNote) {
        setNote("");
      }
      if (!defaultUserIds?.length) {
        setSelectedUserIds([]);
        setSelectAll(false);
      }
    }

    if (isContentLoading || defaultsAppliedRef.current) {
      return;
    }

    if (defaultUserIds?.length > 0) {
      if (users.length === 0) {
        return;
      }
      setSelectedUserIds(getDefaultNotifyUserIdsWithEmail(users, defaultUserIds));
      defaultsAppliedRef.current = true;
    }
  }, [visible, showNote, isContentLoading, users, defaultUserIds]);
  // Update selectAll state when selectedUserIds changes
  useEffect(() => {
    if (filteredUsers.length > 0) {
      setSelectAll(selectedUserIds.length === filteredUsers.length && filteredUsers.length > 0);
    } else {
      setSelectAll(false);
    }
  }, [selectedUserIds, filteredUsers.length]);

  const handleUserToggle = (userId) => {
    setSelectedUserIds((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((id) => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      const filteredUserIds = filteredUsers.map((user) => user.id || user._id);
      setSelectedUserIds((prev) => prev.filter((id) => !filteredUserIds.includes(id)));
    } else {
      const filteredUserIds = filteredUsers.map((user) => user.id || user._id);
      setSelectedUserIds((prev) => [...new Set([...prev, ...filteredUserIds])]);
    }
    setSelectAll(!selectAll);
  };

  const handleNotify = () => {
    if (selectedUserIds.length === 0) {
      notification.warning({
        message: "No users selected",
        description: "Please select at least one user to notify.",
      });
      return;
    }

    // Ensure all user IDs are strings
    const stringUserIds = selectedUserIds.map((id) => String(id));
    onNotify(stringUserIds, showNote ? note.trim() : undefined);
  };
  const handleClose = () => {
    setSelectedUserIds([]);
    setSelectAll(false);
    setSearchText("");
    if (showNote) {
      setNote("");
    }
    onClose();
  };
  if (!selectedProject || !projectId) {
    return null;
  }

  const displayName = itemName || folder?.name || folder?.title || "Item";

  return (
    <Modal
      title={`Notify Users - ${displayName}`}
      open={visible}
      onCancel={handleClose}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          Cancel
        </Button>,
        <Button
          key="notify"
          type="primary"
          onClick={handleNotify}
          disabled={isContentLoading || selectedUserIds.length === 0}
          loading={isContentLoading}
        >
          Send Notification ({selectedUserIds.length})
        </Button>,
      ]}
      width={600}
    >
      {isContentLoading ? (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
      <div style={{ marginBottom: 16 }}>
        <Button
          type="link"
          onClick={handleSelectAll}
          style={{ padding: 0 }}
        >
          {selectAll ? "Deselect All" : "Select All"}
        </Button>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Search users by name, email, vendor, or role..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
        />
      </div>

      {showNote && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>Note</div>
          <TextArea
            placeholder="Add an optional note to include in the notification email..."
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            maxLength={2000}
            showCount
          />
        </div>
      )}

      <div
          style={{
            maxHeight: "400px",
            overflowY: "auto",
            border: "1px solid #d9d9d9",
            borderRadius: "4px",
            padding: "12px",
          }}
        >
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            {filteredUsers.length === 0 ? (
              <div style={{ textAlign: "center", padding: "20px", color: "#999" }}>
                {searchText ? "No users found matching your search." : "No users found in this project"}
              </div>
            ) : (
              filteredUsers.map((user) => {
                const userId = user.id || user._id;
                const isSelected = selectedUserIds.includes(userId);
                const displayName = user.username || `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email;
                const role = user.formatted_role || user.role || "";
                const vendorName = user.vendor_name ? ` (${user.vendor_name})` : "";

                return (
                  <Checkbox
                    key={userId}
                    checked={isSelected}
                    onChange={() => handleUserToggle(userId)}
                  >
                    <div>
                      <div style={{ fontWeight: 500 }}>{displayName}</div>
                      {(role || vendorName) && (
                        <div style={{ fontSize: "12px", color: "#999", marginTop: "2px" }}>
                          {role}
                          {role && vendorName && " • "}
                          {vendorName}
                        </div>
                      )}
                    </div>
                  </Checkbox>
                );
              })
            )}
          </Space>
        </div>
        </>
      )}
    </Modal>
  );
};

export default NotifyUsersModal;

